import { Popover, PopoverProps } from "@dropbox/dig-components/popover";
import { Box } from "@dropbox/dig-foundations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashProfileCard } from "@mirage/analytics/events/types/click_dash_profile_card";
import { PAP_Shown_DashProfileCard } from "@mirage/analytics/events/types/shown_dash_profile_card";
import { BasicUserProfileCard } from "@mirage/mosaics/BaseProfilePage/BasicUserProfileCard";
import { getUserProfilePageHref } from "@mirage/mosaics/UserProfilePage/utils";
import { usePersonFromOrgChartByEmail } from "@mirage/service-people/hooks";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export const UserProfileHoverCard = ({
  personEmail,
  children,
  className,
  fallbackChildren,
  placement = "top-start",
}: {
  personEmail?: string;
  children: React.ReactNode;
  className?: string;
  fallbackChildren?: React.ReactNode;
  placement?: PopoverProps["placement"];
}) => {
  const person = usePersonFromOrgChartByEmail(personEmail);
  const { reportPapEvent } = useMirageAnalyticsContext();

  const [boundaryEl, setBoundaryEl] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const navigate = useNavigate();
  const onViewProfile = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      e.stopPropagation();

      reportPapEvent(
        PAP_Click_DashProfileCard({
          featureLine: "people",
          profileCardType: "hover_card",
        }),
      );

      if (person?.displayHandle) {
        const href = getUserProfilePageHref(person.displayHandle);
        if (href) {
          navigate(href);
        }
      }
    },
    [person?.displayHandle, navigate],
  );

  useEffect(() => {
    // Set the boundary element to the body so that the popover can be displayed
    // outside of hidden/clipped viewports
    setBoundaryEl(document.querySelector("body"));
  }, [setBoundaryEl]);

  useEffect(() => {
    // Prefetch profile photo for hover card
    if (person?.profilePhotoUrl) {
      const img = new Image();
      img.src = person.profilePhotoUrl;
    }
  }, [person?.profilePhotoUrl]);

  useEffect(() => {
    // Log whenever we display hover card
    if (isHovered) {
      reportPapEvent(
        PAP_Shown_DashProfileCard({
          featureLine: "people",
          profileCardType: "hover_card",
        }),
      );
    }
  }, [isHovered]);

  if (!person) return <>{fallbackChildren ? fallbackChildren : children}</>;

  return (
    <Popover
      boundaryEl={boundaryEl}
      placement={placement}
      triggerStrategy="hover"
      open={isHovered}
      onToggle={setIsHovered}
      hoverBufferTime={50}
      isPortaled
    >
      <Popover.Trigger asChild>
        <Box
          as="div"
          position="relative"
          display="inline-block"
          className={className}
        >
          {children}
        </Box>
      </Popover.Trigger>
      <Popover.Overlay>
        <Popover.Content asChild>
          <Box>
            <BasicUserProfileCard
              person={person}
              onViewProfile={onViewProfile}
            />
          </Box>
        </Popover.Content>
      </Popover.Overlay>
    </Popover>
  );
};
